"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, ClipboardCheck, RefreshCw, Search } from "lucide-react";
import { DataTable, type Column } from "../DataTable";
import { useERP, formatBDT } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";

export interface ReturnsModuleProps {
  onNavigate: (pageId: string) => void;
}

export function ReturnsModule({ onNavigate }: ReturnsModuleProps) {
  const erp = useERP();
  const { toast } = useToast();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">Returns (RMA)</h1>
          <p className="text-muted-foreground mt-1 text-sm">Sales returns with inspection, credit notes and stock reversal.</p>
        </div>
        <Button size="sm" onClick={() => onNavigate("returns-new")}><Plus className="size-4" /> New RMA</Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Total RMAs</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{erp.salesReturns.length}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Pending Authorization</CardTitle></CardHeader>
          <CardContent><div className="text-destructive text-2xl font-semibold">{erp.salesReturns.filter(r => r.status === "Requested").length}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Credit Issued</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{erp.salesReturns.filter(r => r.status === "Credit Note Issued").length}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Total Credit</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{formatBDT(erp.salesReturns.reduce((s, r) => s + r.creditAmount, 0))}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>All RMAs</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "rmaNumber", header: "RMA #", sortable: true, render: r => <span className="font-mono text-xs text-primary">{r.rmaNumber}</span> },
              { key: "customer", header: "Customer", sortable: true, render: r => erp.getCustomer(r.customerId)?.name ?? "—" },
              { key: "invoice", header: "Original Invoice", render: r => erp.salesInvoices.find(i => i.id === r.originalInvoiceId)?.invoiceNumber ?? "—" },
              { key: "returnDate", header: "Return Date", sortable: true },
              { key: "reason", header: "Reason" },
              { key: "creditAmount", header: "Credit Amount", align: "right", sortable: true, render: r => formatBDT(r.creditAmount) },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Credit Note Issued" ? "default" : r.status === "Rejected" ? "destructive" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="outline" disabled={r.status !== "Requested"}
                    onClick={() => { erp.authorizeReturn(r.id); toast({ title: "RMA Authorized", description: `${r.rmaNumber} has been authorized for inspection.` }); }}>
                    <ClipboardCheck className="size-3.5" /> Authorize
                  </Button>
                  <Button size="sm" variant="outline" disabled={r.status !== "Authorized" && r.status !== "Inspected"}
                    onClick={() => { erp.issueCreditNote(r.id); toast({ title: "Credit Note Issued", description: `${r.rmaNumber} — credit note posted.` }); }}>
                    <RefreshCw className="size-3.5" /> Credit Note
                  </Button>
                </div>
              ) },
            ] as Column<typeof erp.salesReturns[number]>[]}
            data={erp.salesReturns}
            rowKey={r => r.id}
            searchKeys={["rmaNumber", "reason"]}
            onRowClick={r => onNavigate(`rma-detail:${r.id}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
